// src/pages/vendor/Dashboard.js
import React,{useEffect,useState} from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import InventoryIcon from "@mui/icons-material/Inventory";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PolicyIcon from '@mui/icons-material/Policy';
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import WarningIcon from "@mui/icons-material/Warning";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  Pie,
  PieChart,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const VendorDashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [summary, setSummary] = useState({
  totalProducts: 0,
  totalOrders: 0,
  totalRevenue: 0,
  lowStockCount: 0,
  outOfStockCount: 0,
});

const [analytics, setAnalytics] = useState({
  monthlySales: [],
  weeklyRevenue: [],
  topProducts: [],
});

const [unansweredQuestionsCount, setUnansweredQuestionsCount] = useState(0);

useEffect(() => {
  const fetchSummary = async () => {
    try {
      const { data } = await api.get(
        "vendor/dashboard/summary"
      );
      setSummary(data);
    } catch (err) {
      console.error("Dashboard summary failed", err);
    }
  };

  fetchSummary();
}, []);

useEffect(() => {
  const fetchAnalytics = async () => {
  try {
    const { data } = await api.get("vendor/dashboard/analytics");
    setAnalytics(data);
  } catch (err) {
    console.error("Analytics failed", err);
  }
};

fetchAnalytics();
}, []); // Fetch analytics data similarly when needed

const fetchUnansweredQuestionsCount = async () => {
  try {
    const { data } = await api.get("/vendor/dashboard/unanswered-questions-count");
    setUnansweredQuestionsCount(data.count || 0);
  } catch (err) {
    console.error("Failed to fetch unanswered questions count", err);
  }
};

useEffect(() => {
  fetchUnansweredQuestionsCount();
}, []);

  const currencyFormatter = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const cards = [
  {
    key: "products",
    icon: <InventoryIcon sx={{ fontSize: 42, color: "#ff9900" }} />,
    title: `${summary.totalProducts} Products`,
    subtitle: "Active in your shop",
   onClick: () => navigate("/vendor/products?type=active"),
  },
  {
    key: "orders",
    icon: <ReceiptLongIcon sx={{ fontSize: 42, color: "#1976d2" }} />,
    title: `${summary.totalOrders} Orders`,
    subtitle: "Total orders received",
    onClick: () => navigate("/vendor/orders"),
  },
  {
    key: "revenue",
    icon: <TrendingUpIcon sx={{ fontSize: 42, color: "#4caf50" }} />,
    title: `₹${Number(summary.totalRevenue || 0).toLocaleString("en-IN")}`,
    subtitle: "Total revenue",
  },
  {
    key: "lowStock",
    icon: <WarningIcon sx={{ fontSize: 42, color: "#ed6c02" }} />,
    title: `${(summary.lowStockCount || 0) + (summary.outOfStockCount || 0)} Low & Out of Stock`,
    subtitle: "Products needing attention",
    onClick: () => navigate("/vendor/products?filter=low+out"),
  },
  {
    key: "questions",
    icon: <QuestionAnswerIcon sx={{ fontSize: 42, color: "#9c27b0" }} />,
    title: `${unansweredQuestionsCount} Unanswered`,
    subtitle: "Questions need your response",
    onClick: () => navigate("/vendor/unanswered-questions"),
  },
];


  const COLORS = ["#ff9900", "#1976d2", "#4caf50"];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", background: "#f5f5f5" }}>
      <Typography variant="h5" fontWeight={700} mb={3}>
        Vendor Dashboard
      </Typography>

      {/* ================= SUMMARY CARDS ================= */}
      <Grid container spacing={3}>
  {cards.map((item) => (
    <Grid item xs={12} sm={6} md={4} key={item.key}>
      <Card
        sx={{
          borderRadius: 3,
          cursor: item.onClick ? "pointer" : "default",
          transition: "0.2s",
          "&:hover": item.onClick
            ? { boxShadow: 6, transform: "translateY(-2px)" }
            : {},
        }}
        onClick={item.onClick}
      >
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            {item.icon}
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.subtitle}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  ))}
</Grid>



      {/* ================= MANAGE STORE ================= */}
      <Box mt={5}>
        <Typography variant="h6" fontWeight={600} mb={2}>
          Manage Store
        </Typography>

        <Grid container spacing={3}>
          {[
            {
              icon: <ReceiptLongIcon sx={{ fontSize: 50 }} />,
              title: "Manage Orders",
              desc: "View & process customer orders",
              action: "/vendor/orders",
              label: "View Orders",
            },
            {
              icon: <AddBusinessIcon sx={{ fontSize: 50 }} />,
              title: "Add New Product",
              desc: "Add fresh products to your catalog",
              action: "/vendor/products/new",
              label: "Add Product",
            },
            {
              icon: <InventoryIcon sx={{ fontSize: 50 }} />,
              title: "View All Products",
              desc: "Manage & edit your product list",
              action: "/vendor/products?type=all",
              label: "My Products",
            },
            {
              icon: <InventoryIcon sx={{ fontSize: 50, color: "#9e9e9e" }} />,
              title: "Archived Products",
              desc: "View deleted / archived items",
              action: "/vendor/products/archived",
              label: "View Archived",
            },
            {
              icon: <InventoryIcon sx={{ fontSize: 50, color: "#1976d2" }} />,
              title: "My Activity",
              desc: "View all your store activities",
              action: "/vendor/my-activity",
              label: "View Activity",
            },
             {
              icon: <PolicyIcon sx={{ fontSize: 50, color: "#000000" }} />,
              title: "Store Policies",
              desc: "View & manage your store policies",
              action: "/vendor/policies",
              label: "View Policies",
            }
          ].map((c, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card sx={{ borderRadius: 3, boxShadow: 2, p: 2 }}>
                {c.icon}
                <Typography variant="h6" fontWeight={600} mt={1}>
                  {c.title}
                </Typography>
                <Typography color="text.secondary" mb={2}>
                  {c.desc}
                </Typography>
                <Button
                  fullWidth={isMobile}
                  variant="contained"
                  sx={{
                    background: "#ff9900",
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                  onClick={() => navigate(c.action)}
                >
                  {c.label}
                </Button>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ================= ANALYTICS (DESKTOP ONLY) ================= */}
      {!isMobile && (
        <Box mt={6}>
          <Typography variant="h6" fontWeight={700} mb={2}>
            Analytics Overview
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={2} sm={6}>
              <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2,width:"400px" }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Monthly Sales
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart 
                  data={analytics.monthlySales.map((m) => ({
                          month: `M${m._id}`,
                          sales: m.totalSales,
                        }))}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={currencyFormatter} />
                    <Tooltip formatter={(value) => currencyFormatter(value)} />
                    <Line type="monotone" dataKey="sales" stroke="#ff9900" />
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2,width:"400px" }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Weekly Revenue
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.weeklyRevenue}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="day" />
                    <YAxis tickFormatter={currencyFormatter} />
                    <Tooltip formatter={(value) => currencyFormatter(value)} />
                    <Bar dataKey="amount" fill="#1976d2" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2,width:"400px" }}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>
                  Top Selling Products
                </Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={analytics.topProducts}
                      dataKey="orders"
                      nameKey="name"
                      // cx="50%"
                      // cy="50%"
                      outerRadius={80}
                      // labelLine={false}
                      // paddingAngle={3}
                    >
                      {analytics.topProducts.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default VendorDashboard;